import { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as React from "react";
import { Modal } from "react-bootstrap";
import Select, { components } from "react-select";

import Likes from "../../../Likes";
import {
    REMOVE_TOOL_SETUP_LABEL,
    UPDATE_TOOL_SETUP_LABEL,
    UPDATE_TOOL_SETUP_LABELS_SESSION_OFFSET,
    UPDATE_TOOL_SETUP_LABELS_HISTORY_KEYS,
    updateToolSetupLabels,
} from "../reducers/insights";
import { RECALCULATE_REPORT } from "../reducers/linesOrders";
import { getBackend } from "../../../../lib/backend/Backend2";
import { getLoggedinUser } from "../../../../lib/Auth";
import { translate } from "../../../IntlProviderWrapper";
import { classNames } from "../../../../lib/Util";
import ErrorComponent from "../../../ErrorComponent";
import {
    CLEANING_DURATIONS,
    CLEANING_RULE,
    CLEANING_RULE_LABEL,
    CLEANING_TYPE,
    CLEANING_TYPE_LABEL,
} from "../../../../lib/Models";

import type { Props } from "react-select/src/Select";
import type { OptionProps } from "react-select/src/components/Option";
import type { ToolSetupLabel as ToolSetupLabelType, InsightsActionsTypes } from "../reducers/insights";
import type { UseDispatchHook, ILabelValuePair } from "../../../../lib/Util";
import type { ReduxState } from "../reducers/reducers";
import type { RecalculateReport } from "../reducers/linesOrders";

import "./ToolSetupLabel.scss";

const TOOL_SETUP_LABELS_FILE = "tool_setup_active_learning.csv";

/**
 * Fetches the tool setup label history from blob storage.
 *
 * @returns {Promise<string | null>} The content of the tool setup label history file, or null if not found.
 */
const getToolSetupLabelHistory = async (): Promise<string | null> => {
    try {
        const { content } = await getBackend().common.getBlobStorageItemAsStringOrNull({
            key: TOOL_SETUP_LABELS_FILE,
        });
        return content;
    } catch (error) {
        console.error("Failed to fetch tool setup label history", error);
    }

    return null;
};

const saveToolSetupLabelHistory = async (content: string): Promise<void> => {
    try {
        await getBackend().common.setBlobStorageItemAsString({
            key: TOOL_SETUP_LABELS_FILE,
            content,
        });
    } catch (error) {
        console.error("Failed to save tool setup label history", error);
    }
};

/**
 * Saves the tool setup label history to blob storage.
 *
 * @param {ToolSetupLabelType} label - The new tool setup label to be saved.
 * @param {ToolSetupLabelType[]} labels - The existing tool setup labels (from current session).
 * @param {number} session_offset - The offset of the labels from current session in blob storage file. Used
 * to preserve content from previous sessions.
 * @returns {Promise<void>}
 */
const updateAndSaveToolSetupLabelHistory = async (
    label: ToolSetupLabelType,
    labels: ToolSetupLabelType[],
    session_offset: number
): Promise<void> => {
    const tool_setup_labels = updateToolSetupLabels(labels, label);
    tool_setup_labels.sort((a, b) => a.ts - b.ts);

    // Prepare new content for blob storage
    const user_uuid = getLoggedinUser().uuid;
    const content = (await getToolSetupLabelHistory()) ?? "";
    const lines = content.split("\n");
    const new_lines = lines
        // Preserve content from previous sessions
        .slice(0, session_offset)
        // Append new labels from current session
        .concat(
            tool_setup_labels.map(l => {
                const date = new Date(l.ts).toISOString();
                const comment = JSON.stringify(l.comment);
                return [
                    l.previous_material_id,
                    l.next_material_id,
                    l.class,
                    date,
                    user_uuid,
                    l.cleaning_type,
                    comment,
                    l.line_external_id,
                    l.cleaning_rule,
                ].join(",");
            })
        );

    await saveToolSetupLabelHistory(new_lines.join("\n"));
};

/**
 * Removes a tool setup label based on the provided key and session offset.
 *
 * @param {string} key - The key identifying the tool setup label to be removed.
 * @param {number} session_offset - The offset of the labels from current session in blob storage file.
 * @returns {Promise<number>} - A promise that resolves to a number indicating the result of the removal operation.
 */
const removeToolSetupLabel = async (key: string, session_offset: number): Promise<number> => {
    const content = (await getToolSetupLabelHistory()) ?? "";
    const lines = content.split("\n");
    const new_lines: string[] = [];
    let new_offset = 0;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (getToolSetupKeyFromLine(line) !== key) {
            new_lines.push(line);
            if (i < session_offset) {
                new_offset++;
            }
        }
    }

    await saveToolSetupLabelHistory(new_lines.join("\n"));
    return new_offset;
};

/**
 * Generates a key for tool setup based on the previous and current material external IDs.
 *
 * @param {string} [prev_material_external_id=""] - The external ID of the previous material. Defaults to an empty string.
 * @param {string} [material_external_id=""] - The external ID of the current material. Defaults to an empty string.
 * @returns {string} A string key composed of the previous and current material external IDs, separated by a comma.
 */
const getToolSetupKey = (prev_material_external_id = "", material_external_id = ""): string => {
    return [prev_material_external_id, material_external_id].join(",");
};

/**
 * Extracts tool setup key from a line string.
 *
 * @param {string} line - A string beginning with two comma-separated values representing previous and current material external IDs.
 * @returns {string} - The tool setup key generated from the provided material external IDs.
 */
const getToolSetupKeyFromLine = (line: string): string => {
    const [prev_material_external_id, material_external_id] = line.split(",");
    return getToolSetupKey(prev_material_external_id, material_external_id);
};

/* -------------------------------------------------------------------------------------------------
 * ToolSetupLabel
 * -----------------------------------------------------------------------------------------------*/

type ToolSetupLabelProps = {
    material_external_id: string;
    prev_material_external_id: string;
    line_external_id: string;
};

const ToolSetupLabel = (props: ToolSetupLabelProps) => {
    const { material_external_id, prev_material_external_id, line_external_id } = props;

    const [show_dialog, setShowDialog] = useState<boolean>(false);
    const [value, setValue] = useState<number>(-1);

    const dispatch: UseDispatchHook<InsightsActionsTypes | RecalculateReport> = useDispatch();
    const session_offset: number = useSelector(
        (state: ReduxState) => state.gantt_chart_insights.tool_setup_labels_session_offset
    );
    const history_keys: string[] = useSelector(
        (state: ReduxState) => state.gantt_chart_insights.tool_setup_labels_history_keys
    );
    const tool_setup_labels: ToolSetupLabelType[] = useSelector(
        (state: ReduxState) => state.gantt_chart_insights.tool_setup_labels
    );
    const tool_setup_label: ToolSetupLabelType | void = useSelector((state: ReduxState) => {
        const { tool_setup_labels } = state.gantt_chart_insights;
        return tool_setup_labels.find(
            label =>
                label.previous_material_id === prev_material_external_id &&
                label.next_material_id === material_external_id
        );
    });
    const history_keys_set: Set<string> = useMemo(() => new Set(history_keys), [history_keys]);
    const tool_setup_key = getToolSetupKey(prev_material_external_id, material_external_id);
    const has_history = tool_setup_label !== undefined || history_keys_set.has(tool_setup_key);

    const handleLike = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setValue(1);
        setShowDialog(true);
    };

    const handleDislike = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setValue(0);
        setShowDialog(true);
    };

    let handleClear: ((event: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
    if (has_history) {
        handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            setValue(-1);
            setShowDialog(true);
        };
    }

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const handleSave = async (cleaning_type: string, cleaning_rule: string, comment: string) => {
        const new_label: ToolSetupLabelType = {
            previous_material_id: prev_material_external_id,
            next_material_id: material_external_id,
            class: value,
            ts: Date.now(),
            cleaning_type,
            cleaning_rule,
            comment,
            line_external_id,
        };
        await updateAndSaveToolSetupLabelHistory(new_label, tool_setup_labels, session_offset);

        dispatch({
            type: UPDATE_TOOL_SETUP_LABEL,
            data: new_label,
        });

        dispatch({ type: RECALCULATE_REPORT, data: undefined });

        setShowDialog(false);
    };

    const handleRemove = async () => {
        const new_offset = await removeToolSetupLabel(tool_setup_key, session_offset);
        dispatch({ type: UPDATE_TOOL_SETUP_LABELS_SESSION_OFFSET, data: new_offset });

        const new_history_keys = history_keys.filter(key => key !== tool_setup_key);
        dispatch({ type: UPDATE_TOOL_SETUP_LABELS_HISTORY_KEYS, data: new_history_keys });

        dispatch({
            type: REMOVE_TOOL_SETUP_LABEL,
            data: {
                previous_material_id: prev_material_external_id,
                next_material_id: material_external_id,
                class: -1,
                ts: 0,
                cleaning_type: "",
                cleaning_rule: "",
                comment: "",
                line_external_id: "",
            },
        });

        dispatch({ type: RECALCULATE_REPORT, data: undefined });

        setShowDialog(false);
    };

    const handleClickEvents = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    };

    return (
        <div className="tool-setup-label" onClick={handleClickEvents}>
            <Likes
                title="Tool Setup"
                like_label={material_external_id}
                dislike_label={prev_material_external_id}
                clear_label={translate("common.remove", "Remove")}
                likes={tool_setup_label?.class === 1 ? 1 : 0}
                dislikes={tool_setup_label?.class === 0 ? 1 : 0}
                show_counts={false}
                onLike={handleLike}
                onDislike={handleDislike}
                onClear={handleClear}
            />
            <ToolSetupLabelDialog
                show={show_dialog}
                material_external_id={material_external_id}
                label={value}
                line_external_id={line_external_id}
                onChange={setValue}
                onClose={handleCloseDialog}
                onSave={handleSave}
                onRemove={handleRemove}
            />
        </div>
    );
};

/* -------------------------------------------------------------------------------------------------
 * ToolSetupLabelDialog
 * -----------------------------------------------------------------------------------------------*/

const SelectOptionWithIcon = (props: OptionProps) => {
    return (
        <components.Option {...props}>
            <i className={props.data.icon} />
            <span>{props.data.label}</span>
        </components.Option>
    );
};

const SelectControlWithIcon = (props: Props) => {
    const { children, ...rest } = props;
    return (
        <components.Control {...rest}>
            <i
                className={classNames(`value-${props.selectProps.value.value} fas`, {
                    "fa-check-circle": props.selectProps.value.value === 1,
                    "fa-times-circle": props.selectProps.value.value === 0,
                })}
            />
            {children}
        </components.Control>
    );
};

type ToolSetupLabelOption = {
    value: number;
    label: string;
    icon: string;
};

const tool_setup_label_options: ToolSetupLabelOption[] = [
    {
        value: 1,
        label: translate("common.yes", "Yes"),
        icon: "far fa-check-circle",
    },
    {
        value: 0,
        label: translate("common.no", "No"),
        icon: "far fa-times-circle",
    },
];

const cleaning_rule_options: ILabelValuePair[] = Object.keys(CLEANING_RULE).map(rule => ({
    value: rule,
    label: CLEANING_RULE_LABEL[rule] ?? rule,
}));

type ToolSetupLabelDialogProps = {
    show: boolean;
    material_external_id: string;
    label: number;
    line_external_id: string;
    onChange: (label: number) => void;
    onClose: () => void;
    onSave: (cleaning_type: string, cleaning_rule: string, comment: string) => Promise<void>;
    onRemove: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
};

const ToolSetupLabelDialog = (props: ToolSetupLabelDialogProps) => {
    const { show, material_external_id, label, line_external_id, onClose, onSave, onRemove } = props;

    const [cleaning_type, setCleaningType] = useState<string>("");
    const [cleaning_rule, setCleaningRule] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [is_cleaning_type_invalid, setIsCleaningTypeInvalid] = useState<boolean>(false);

    const selected_tool_setup_label_option = tool_setup_label_options.find(option => option.value === label);

    const handleCleaningTypeChange = (option: ILabelValuePair | null) => {
        setCleaningType(option?.value ?? "");
        setIsCleaningTypeInvalid(option === null && selected_tool_setup_label_option?.value === 1);
    };

    const handleCleaningRuleChange = (option: ILabelValuePair | null) => {
        setCleaningRule(option?.value ?? "");
    };

    const handleCommentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(event.currentTarget.value);
    };

    const handleFormSubmit = (event: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (cleaning_type === "" && selected_tool_setup_label_option?.value === 1) {
            setIsCleaningTypeInvalid(true);
            return;
        }

        onSave(cleaning_type, cleaning_rule, comment);
    };

    const cleaning_type_options: ILabelValuePair[] = useMemo(() => {
        const line_cleaning_types: Set<string> = new Set();
        for (const cleaning_type_id of Object.keys(CLEANING_DURATIONS)) {
            const line_cleaning_durations = CLEANING_DURATIONS[cleaning_type_id];
            if (line_cleaning_durations !== undefined && line_cleaning_durations[line_external_id] !== undefined) {
                line_cleaning_types.add(cleaning_type_id);
            }
        }

        return line_cleaning_types.size > 0
            ? Array.from(line_cleaning_types).map(type => ({
                  value: type,
                  label: CLEANING_TYPE_LABEL[type] ?? type,
              }))
            : Object.keys(CLEANING_TYPE)
                  .filter(type => type !== CLEANING_TYPE.unknown)
                  .map(type => ({
                      value: type,
                      label: CLEANING_TYPE_LABEL[type] ?? type,
                  }));
    }, [line_external_id]);

    const selected_cleaning_type_option: ILabelValuePair | undefined = useMemo(() => {
        return cleaning_type_options.find(option => option.value === cleaning_type);
    }, [cleaning_type, cleaning_type_options]);

    const selected_cleaning_rule_option: ILabelValuePair | undefined = useMemo(() => {
        return cleaning_rule_options.find(option => option.value === cleaning_rule);
    }, [cleaning_rule]);

    useEffect(() => {
        if (label === 0) {
            setIsCleaningTypeInvalid(false);
        }
    }, [label]);

    return (
        <Modal size="sm" show={show} animation={false} onHide={onClose} className="tool-setup-label-dialog">
            <Modal.Header>
                <Modal.Title>
                    {translate("common.tool_setup", "Tool setup")}
                    {material_external_id !== "" && `: ${material_external_id}`}
                </Modal.Title>
                <button type="button" className="close" onClick={onClose}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{translate("common.close", "Close")}</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                {label < 0 ? (
                    <ErrorComponent
                        type="warning"
                        msg={translate(
                            "ToolSetupLabel.remove_label_warning",
                            "Are you sure you want to remove this label and all its history?"
                        )}
                    />
                ) : (
                    <form
                        id="tool-setup-label-dialog-form"
                        onSubmit={handleFormSubmit}
                        className="tool-setup-label-dialog__form"
                    >
                        <div className="form-group">
                            <label htmlFor="tool-setup-label-value">
                                {translate("common.tool_setup", "Tool setup")}
                            </label>
                            <Select
                                inputId="tool-setup-label-value"
                                classNamePrefix="react-select"
                                className="tool-setup-label-dialog__select--value"
                                components={{ Control: SelectControlWithIcon, Option: SelectOptionWithIcon }}
                                options={tool_setup_label_options}
                                value={selected_tool_setup_label_option}
                                onChange={option => props.onChange(option.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="tool-setup-label-type">
                                {translate("common.cleaning_type", "Cleaning type")}
                            </label>
                            <Select
                                inputId="tool-setup-label-type"
                                classNamePrefix="react-select"
                                className={classNames("tool-setup-label-dialog__select--type", {
                                    "is-invalid": is_cleaning_type_invalid,
                                })}
                                options={cleaning_type_options}
                                value={selected_cleaning_type_option}
                                onChange={handleCleaningTypeChange}
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="tool-setup-label-rule">{translate("common.rule", "Rule")}</label>
                            <Select
                                inputId="tool-setup-label-rule"
                                classNamePrefix="react-select"
                                className={"tool-setup-label-dialog__select--rule"}
                                options={cleaning_rule_options}
                                value={selected_cleaning_rule_option}
                                onChange={handleCleaningRuleChange}
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="tool-setup-label-comment">{translate("common.comment", "Comment")}</label>
                            <textarea
                                className="form-control"
                                id="tool-setup-label-comment"
                                name="comment"
                                rows={3}
                                value={comment}
                                onChange={handleCommentChange}
                            />
                        </div>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-outline-secondary" onClick={onClose} autoFocus={label < 0}>
                    {translate("common.cancel", "Cancel")}
                </button>
                {label < 0 ? (
                    <button className="btn btn-danger" onClick={onRemove}>
                        {translate("common.remove", "Remove")}
                    </button>
                ) : (
                    <button
                        className="btn btn-primary"
                        type="submit"
                        form="tool-setup-label-dialog-form"
                        onClick={handleFormSubmit}
                    >
                        {translate("common.save", "Save")}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

/* -------------------------------------------------------------------------------------------------
 * ToolSetupLabelHistory
 * -----------------------------------------------------------------------------------------------*/

const ToolSetupLabelHistory = () => {
    const dispatch: UseDispatchHook<InsightsActionsTypes> = useDispatch();
    useEffect(() => {
        const fetchHistory = async () => {
            const content = await getToolSetupLabelHistory();
            if (content !== null) {
                const lines = content.split("\n");
                const session_offset = lines.length;
                dispatch({ type: UPDATE_TOOL_SETUP_LABELS_SESSION_OFFSET, data: session_offset });

                const history_keys_set: Set<string> = new Set();
                for (const line of lines) {
                    history_keys_set.add(getToolSetupKeyFromLine(line));
                }

                dispatch({ type: UPDATE_TOOL_SETUP_LABELS_HISTORY_KEYS, data: Array.from(history_keys_set) });
            }
        };
        fetchHistory();
    }, []);

    return null;
};

export default ToolSetupLabel;
export { ToolSetupLabelHistory };
export type { ToolSetupLabelProps };
